import { boostApi } from '@api/queries/boost';
import { workshopApi } from '@api/queries/workshop';
import { type BoostItemType, type BoostType } from '@common/types/factoryType';
import {
  type ClaimGoodsByIdResponseType,
  type GetWorkshopResponseType,
} from '@common/types/query/workshop';
import {
  type UserWorkshop,
  type UserWorkshopNextToBuild,
} from '@common/types/workshopType';
import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';

import { sanitizeBoosts } from '@/common/lib/sanitizeBoosts';
import { type BoostInstantCompleteResponse } from '@/common/types/query/boost';

interface WorkshopInitialState {
  list: UserWorkshop[] | null;
  nextToBuild: UserWorkshopNextToBuild | null;
  boosts: BoostItemType[] | null;
}

const initialState: WorkshopInitialState = {
  list: null,
  nextToBuild: null,
  boosts: null,
};

const workshopSlice = createSlice({
  initialState,
  name: 'workshop',
  reducers: {
    addProductedForGood: (state, { payload }) => {
      const workshop = state.list?.find((el) => el.id === payload.id);

      if (workshop) {
        const good = workshop.products.find(
          (el) => el.name === workshop.targetProduct,
        );

        if (good) {
          good.nowProducted = Math.min(payload.producted, good.storageCapacity);
        }
      }
    },
    clearWorkshopBoost: (
      state,
      { payload }: PayloadAction<{ plantId: string; boostType: BoostType }>,
    ) => {
      if (state.boosts) {
        const boost = state.boosts.find(
          (boost) =>
            boost?.boostType === payload.boostType &&
            boost?.plants?.includes(payload.plantId),
        );

        if (boost) {
          boost.expiresInSeconds = 0;
        }
      }
    },
    countDowntWorkshopBoostTimers: (state) => {
      const boosts = state?.boosts;

      if (boosts) {
        for (const boost of boosts) {
          if (!boost) continue;

          if ((boost.expiresInSeconds || 0) > 0) {
            boost.expiresInSeconds = (boost.expiresInSeconds || 0) - 1;
          }

          if ((boost.nextUseInSeconds || 0) > 0) {
            boost.nextUseInSeconds = (boost.nextUseInSeconds || 0) - 1;
          }
        }
      }
    },
    buyCryptoBoostsWorkshop: (
      state,
      { payload }: PayloadAction<BoostItemType[]>,
    ) => {
      if (state.list) {
        const incoming =
          sanitizeBoosts<BoostItemType>(
            payload,
            'workshop/buyCryptoBoostsWorkshop',
          ) || [];
        state.boosts = state.boosts
          ? [
              ...(sanitizeBoosts<BoostItemType>(
                state.boosts,
                'workshop/buyCryptoBoostsWorkshop/existing',
              ) || []),
              ...incoming,
            ]
          : incoming;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        workshopApi.endpoints.getWorkshop.matchFulfilled,
        (state, { payload }) => {
          state.list = payload.userWorkshops;
          state.nextToBuild = payload.nextToBuild;
          state.boosts = sanitizeBoosts<BoostItemType>(
            payload.boosts,
            'workshop/getWorkshop',
          );
        },
      )
      .addMatcher(
        workshopApi.endpoints.buildWorkshop.matchFulfilled,
        (state, { payload }) => {
          state.list = payload;
        },
      )
      .addMatcher(
        isAnyOf(
          workshopApi.endpoints.putToWorkshop.matchFulfilled,
          workshopApi.endpoints.upgradeWorkshop.matchFulfilled,
          workshopApi.endpoints.clearWorkshopProduction.matchFulfilled,
        ),
        (state, { payload }) => {
          const payloadWorkshop =
            'workshop' in payload ? payload.workshop : payload;

          if (state.list) {
            const workshop = state.list.find(
              ({ id }) => id === payloadWorkshop.id,
            );

            if (workshop) {
              Object.assign(workshop, payloadWorkshop);
            }
          }
        },
      )
      .addMatcher(
        workshopApi.endpoints.claimGoodsById.matchFulfilled,
        (state, { payload }: PayloadAction<ClaimGoodsByIdResponseType>) => {
          if (state.list) {
            const workshop = state.list.find(
              ({ id }) => id === payload.plant.id,
            );

            if (workshop) {
              Object.assign(workshop, payload.plant);
            }
          }
        },
      )
      .addMatcher(
        workshopApi.endpoints.claimAllGoods.matchFulfilled,
        (state, { payload }) => {
          state.list = payload.plants;
        },
      )
      .addMatcher(
        isAnyOf(
          boostApi.endpoints.boostPlant.matchFulfilled,
          boostApi.endpoints.boostPlantAdvertising.matchFulfilled,
        ),
        (state, action) => {
          const plantType = action.meta.arg.originalArgs.plantType;

          if (plantType === 'Workshop') {
            const payload = action.payload as GetWorkshopResponseType;

            state.list = payload.userWorkshops;
            state.nextToBuild = payload.nextToBuild;
            state.boosts = sanitizeBoosts<BoostItemType>(
              payload.boosts,
              'workshop/boostPlant',
            );
          }
        },
      )
      .addMatcher(
        boostApi.endpoints.boostInstantComplete.matchFulfilled,
        (state, { payload }: PayloadAction<BoostInstantCompleteResponse>) => {
          const workshop = state.list?.find(
            ({ id }) => id === payload.plant.id,
          );

          if (workshop) {
            Object.assign(workshop, payload.plant);
          }
        },
      );
  },
});

export const {
  addProductedForGood,
  clearWorkshopBoost,
  countDowntWorkshopBoostTimers,
  buyCryptoBoostsWorkshop,
} = workshopSlice.actions;
export default workshopSlice.reducer;
